import { useFormContext, Controller } from 'react-hook-form'
import { isEmpty } from '@myap/ui-library'
import Autosuggest from '../../../_common/forms/Autosuggest'
import { getSchoolSuggestions } from '../../../../rest/search'
import {
  FIELD_DISPLAY_SCHOOLNAME,
  FIELD_SCHOOLNAME,
  FIELD_SCHOOLID,
  FIELD_ORG_CITY,
  FIELD_ORG_STATE,
  FIELD_ORG_COUNTRY,
  FIELD_ZIP,
  FIELD_CITY,
  FIELD_STATE,
} from '../constants'
import { validateFieldState } from '../validations'

const SCHOOL_NOT_FOUND_NAME = "I can't find my high school"

const dropDownItemFunc = label => {
  const [orgName, orgAddress] = label.split('|')
  if (orgName) {
    return (
      <div style={{ width: '100%' }}>
        <div>
          <strong>{orgName}</strong>
        </div>
        <div>{orgAddress ?? <>&nbsp;</>}</div>
      </div>
    )
  }
  return null
}

const itemLabelFunc = (event, label, value) => {
  const [orgName, orgAddress] = label.split('|')
  const displayOrgName =
    value === -1 ? SCHOOL_NOT_FOUND_NAME : orgName !== null && orgName?.length > 0 ? orgName : ''
  return `${displayOrgName}${
    orgAddress !== null && orgAddress?.length > 0 ? ` (${orgAddress})` : ''
  }`
}

function SchoolSearch({
  disabled,
  attendingOrgInfo,
  setAttendingOrgInfo,
  studentAddress,
  currentEPC,
  selectedEPC,
}) {
  const { control, trigger, setValue, getValues, clearErrors } = useFormContext()

  return (
    <Controller
      control={control}
      name={FIELD_DISPLAY_SCHOOLNAME}
      disabled={disabled}
      rules={{
        required: 'You must select a school from the dropdown.',
        validate: () => {
          // It's possible for the user to change the text shown in the input, if they do
          // that, we'll empty attendingOrgName and attendingOrgId to make sure the only
          // valid value is that which is set when user selects a school from the results
          // dropdown.
          if (
            isEmpty(attendingOrgInfo?.attendingOrgName) &&
            attendingOrgInfo?.attendingOrgId !== -1 &&
            isEmpty(attendingOrgInfo?.attendingOrgId)
          ) {
            return 'You must select a school from the dropdown.'
          }
          return undefined
        },
      }}
      render={({
        field: { onChange, onBlur, value, disabled, name },
        fieldState: { invalid, isDirty, isTouched, error },
      }) => {
        return (
          <Autosuggest
            name={name}
            label={`${
              selectedEPC === currentEPC ? 'School You Attend' : 'School You Attended'
            } (Confirm you select the school with the correct City and State)`}
            value={value}
            placeholder="Select school"
            disabled={disabled}
            itemHeight={56}
            required={true}
            dropDownItemFunc={dropDownItemFunc}
            callBackFunc={(label, value) => {
              // function executed on select
              const [orgName, orgAddress] = label.split('|')
              const [orgCity, orgState] = orgAddress.split(', ')
              setValue(FIELD_DISPLAY_SCHOOLNAME, itemLabelFunc(null, label, value))
              setAttendingOrgInfo({
                ...attendingOrgInfo,
                [FIELD_SCHOOLNAME]: orgName,
                [FIELD_SCHOOLID]: value,
                [FIELD_ORG_CITY]: orgCity,
                [FIELD_ORG_STATE]: orgState,
              })
              clearErrors(FIELD_DISPLAY_SCHOOLNAME)
            }}
            itemLabelFunc={itemLabelFunc}
            fetchSuggestions={searchName => getSchoolSuggestions(searchName, getValues(FIELD_ZIP))}
            processSuggestions={(suggestions = []) => {
              const searchResults = suggestions.map(({ orgId, orgName, city, state }) => ({
                label: `${orgName}|${city ?? ''}${state ? `, ${state}` : ''}`,
                value: orgId,
              }))
              return [
                ...searchResults,
                {
                  label: `Can't find your high school?|${studentAddress[FIELD_CITY]}${
                    !isEmpty(studentAddress[FIELD_STATE]) ? `, ${studentAddress[FIELD_STATE]}` : ''
                  }`,
                  value: -1,
                },
              ]
            }}
            onChange={value => {
              // If user starts typing something, clear out saved attending org
              setAttendingOrgInfo({
                [FIELD_SCHOOLNAME]: '',
                [FIELD_SCHOOLID]: '',
                [FIELD_ORG_CITY]: '',
                [FIELD_ORG_STATE]: '',
                [FIELD_ORG_COUNTRY]: '',
              })
              trigger(FIELD_DISPLAY_SCHOOLNAME)
              onChange(value)
            }}
            onBlur={() => {
              trigger(FIELD_DISPLAY_SCHOOLNAME)
              onBlur()
            }}
            validation={validateFieldState({ invalid, isDirty, isTouched, error, value })}
            validationMsg={error?.message}
            scrollLimit={11}
          />
        )
      }}
    />
  )
}

export default SchoolSearch
